import { Component, ViewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputComponent } from "@components/input/input.component";
import { TableComponent as TableComponent_1 , TableComponent } from "@components/table/table.component";
import { GridOptions } from "ag-grid-community";
import _ from "lodash";
import { firstValueFrom, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { AgGridTableService } from "~app/common/components/table/ag-grid-table.service";
import { RaskColumn } from "~app/common/components/table/models/rask-column";

import { ToastService } from "~app/common/components/toast/toast.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SystemadministrationService } from "../../../systemadministration.service";
import { AddOrUpdateInmatningTestCommand } from "./AddOrUpdateInmatningTestCommand";
import { InmatningStatusar, InmatningstestRowVm } from "./inmatningtest.models";

@Component({
    selector: "rask-inmatningtest",
    templateUrl: "./inmatningtest.component.html",
    styleUrl: "./inmatningtest.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        InputComponent,
        FormsModule,
        ButtonComponent,
        TableComponent_1
    ]
})
export class InmatningtestComponent {
    accessToken: string;
    gridOptions: GridOptions;
    matmetod = "stockm";
    matmetodChoices: IRadiobuttonItem[] = [
        { key: "stockm",
            value: "Stockmätning" },
        { key: "travm",
            value: "Travmätning" }
    ];
    rows: InmatningstestRowVm[] = [];
    tableColumns: RaskColumn[];
    @ViewChild("table") tableComp: TableComponent;
    token: any;
    virkesordernr: string;

    private readonly administrationService = inject(SystemadministrationService);
    private readonly agGridService = inject(AgGridTableService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);

    constructor() {
        this.initGrid();
    }

    protected accessTokenChanged() {
        try {
            const base64Url = this.accessToken.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(window.atob(base64).split("").map((c) => {
                const part1 = (`00${c.charCodeAt(0).toString(16)}`).slice(-2);
                return `%${part1}`;
            }).join(""));
            this.token = JSON.parse(jsonPayload);
        } catch {
            this.token = null;
            this.toastService.add("Ogiltig access token", 3);
        }
    }

    protected canEnqueue = () => this.rows.some((x) => x.status === InmatningStatusar.ny);

    protected canSave = () => this.rows.some((x) => x.isModified);

    protected async copyFromClipboard() {
        try {
            const text = await navigator.clipboard.readText();
            const splits = text.split("\r\n").map(x => x?.trim()).filter(x => ObjectUtils.stringHasValue(x));
            const columns = splits[0].split(",").map((x, index) => ({ header: x.trim(),
                index: index }));
            const rows = splits.slice(1).map((row) => {
                const values = row.split(",");
                return {
                    rowRaw: row,
                    handelseNr: values[columns.find((x) => x.header === "redovisningsnummer").index],
                    feature: values[columns.find((x) => x.header === "feature").index],
                    virkesordernummer: values[columns.find((x) => x.header === "virkesordernummer").index]
                };
            });

            if (rows.length === 0) {
                this.toastService.add("Ingen data hittades i klistrad data", 3);
                return;
            }

            const voNummer = _.uniqBy(rows, "virkesordernummer");
            if (voNummer.length > 1) {
                this.toastService.add("Flera virkesordernummer hittades i klistrad data, vänligen kontrollera och försök igen", 3);
                return;
            }

            if (voNummer[0].virkesordernummer !== this.virkesordernr) {
                this.virkesordernr = voNummer[0].virkesordernummer;
                // Hämta inmatningstest för vald virkesorder
                await this.onVirkesorderSelected();
            }

            let foundRows = 0;
            let modifiedRows = 0;
            let newRows = 0;
            rows.forEach((row) => {
                const vmRow = this.rows.find((x) => x.handelseNr === row.handelseNr);
                if (vmRow) {
                    if (vmRow.feature !== row?.feature || vmRow.parameter !== row?.rowRaw) {
                        vmRow.parameter = row.rowRaw;
                        vmRow.feature = row.feature;
                        vmRow.status = InmatningStatusar.ny;
                        modifiedRows++;
                        vmRow.setModified();
                    }
                    foundRows++;
                } else {
                    newRows++;
                    this.rows.push(InmatningstestRowVm.createNew(row.handelseNr, row.virkesordernummer, row.rowRaw, row.feature));
                }
            });

            this.tableComp.refreshGrid();
            this.toastService.add(`Matchade ${foundRows} rader, modifierade ${modifiedRows} rader och lade till ${newRows} nya rader`, 1);
        } catch (err) {
            this.toastService.add("Kunde inte läsa klistrad data", 3);
            throw err;
        }
    }

    // eslint-disable-next-line sonarjs/function-return-type
    protected jwtTokenInfo = () => {
        if (!this.token) {
            return 0;
        }
        const exp = this.token.exp;
        const now = Date.now() / 1000;
        const expiryMinutes = Math.round(Math.floor(exp - now) / 60);
        return `Expires in ${expiryMinutes} min (${this.token.email})`;
    };

    protected async onVirkesorderSelected() {
        const work = this.busyService.addWork(this.administrationService.query<any[]>({ $type: "FindInmatningTestQuery",
            virkesordernummer: this.virkesordernr }));
        const response = await firstValueFrom(work);
        this.rows = response.map(x => InmatningstestRowVm.createFromDto(
            x.handelseNr,
            x.virkesordernummer,
            x.guid,
            x.parameter,
            x.status,
            DateUtils.formatDateString(x.senastInmatt, DateUtils.datetimeformatWithoutMillis),
            x.leveransnummer,
            x.feature,
            x.log));
    }

    protected validateJwt = () => {
        if (!ObjectUtils.stringHasValue(this.accessToken)) {
            this.toastService.add("Ange en access token för att kunna köa upp", 3);
            return false;
        }

        return true;
    };

    protected runAll = () => {
        if (!this.validateJwt()) {
            return;
        }
        this.rows.filter((x) => x.status === InmatningStatusar.ny || x.status === InmatningStatusar.fel).forEach((x) => {
            x.enqueue(this.accessToken);
        });
        this.tableComp.refreshGrid();
    };

    protected saveAll = () => {
        this.commandService.reset();
        const commands = this.rows.filter((x) => x.isModified).map((x) => new AddOrUpdateInmatningTestCommand((inst) => {
            inst.handelseNr = x.handelseNr;
            inst.parameter = x.parameter;
            inst.feature = x.feature;
            inst.status = x.status;
            inst.guid = x.guid;
            inst.virkesordernummer = x.virkesordernummer;
            inst.accessToken = this.accessToken;
        }));
        commands.forEach((x) => this.commandService.addTransactionCommand(x));
        this.busyService.addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(x => this.rows.forEach(x => x.setPristine()));
    };

    protected virkesorderFormatter = (vo: any) => vo ? vo.virkesordernummer : "";

    protected virkesorderSearch = (input: string) => {
        if (input?.length >= 3) {
            return this.administrationService.query({
                $type: "FindVirkesorderQuery",
                query: input,
                matmetod: this.matmetod
            });
        } else {
            return of([]);
        }
    };

    protected virkesorderViewFormatter = (vo: any) => vo ? `${vo.virkesordernummer}` : null;

    private initGrid() {
        this.gridOptions = {
            ...this.agGridService.getDefaultOptions(true),
            stopEditingWhenCellsLoseFocus: true
        };

        this.tableColumns = [
            {
                headerName: "RNR",
                field: "handelseNr",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Virkesorder",
                field: "virkesordernummer",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Leverans",
                field: "leveransnummer",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Status",
                field: "status",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Senast inmätt",
                field: "senastInmatt",
                filter: false,
                sortable: false,
                minWidth: 70,
                type: "span"
            },
            {
                headerName: "Parameter",
                field: "parameter",
                filter: false,
                sortable: false,
                minWidth: 70,
                editable: true,
                type: "text"
            },
            {
                field: " ",
                filter: false,
                maxWidth: 110,
                headerName: "Mät in",
                sortable: false,
                editable: false,
                type: "button",
                text: "Mät in",
                onClick: (inmatning: InmatningstestRowVm) => {
                    if (!this.validateJwt()) {
                        return;
                    }
                    inmatning.enqueue(this.accessToken);
                    this.tableComp.refreshGrid();
                }
            },
            {
                field: " ",
                filter: false,
                maxWidth: 110,
                headerName: "Log",
                sortable: false,
                editable: false,
                type: "button",
                text: "Log",
                onClick: (inmatning: InmatningstestRowVm) => this.openLog(inmatning)
            }
        ] as RaskColumn[];
    }

    private openLog(inmatning: InmatningstestRowVm) {
        const input = new PromptInput(InputMode.readOnly, {
            bodyField: {
                value: inmatning.log,
                label: "Log",
                type: "text"
            }
        });
        this.raskModalService.prompt("Log", input, "xl").subscribe();
    }
}
